import { Cell } from "./Cell";
import { ApplicantOrInvitee } from "../types";
import { getChangeRoleTooltip } from "../utils";
import { Show } from "shared/components/helpers/Show";
import {
  Action,
  ContextMenuPopover
} from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import {
  ApplicantStatus,
  ApplicantType,
  ApplicationFeeStatus,
  ApplicationStatus,
  Invitee,
  PartnerFeatures
} from "shared/types";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { getFullName } from "shared/utils/get-full-name";
import { compactNumber } from "shared/utils";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize
} from "shared/components";
import { useChatContext } from "stream-chat-react";
import { VerificationProgress } from "./VerificationProgress";
import { hasActionInManualApplication } from "views/applications-id/helpers";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  CashbackIcon,
  ReceiptDollarIcon,
  Archive03Icon,
  ArrowRight02Icon,
  Attachment02Icon,
  BubbleChatIcon,
  UserIcon,
  Navigation03Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { resolveEffectiveStatus } from "../utils";

const FINAL_APPLICATION_STATES = new Set<ApplicationStatus>([
  ApplicationStatus.Canceled,
  ApplicationStatus.Approved,
  ApplicationStatus.Rejected,
  ApplicationStatus.ConditionalApproved
]);

const getRefundTooltip = (applicant: ApplicantOrInvitee) => {
  let role = "";
  if (applicant.role === ApplicantType.Applicant) role = "applicant";
  else if (applicant.role === ApplicantType.CoSigner) role = "guarantor";

  if (
    "applicant_status" in applicant &&
    applicant.applicant_status === ApplicantStatus.Canceled
  )
    return "You can't request a refund for a canceled application";

  if (
    "application_fee_status" in applicant &&
    "application_status" in applicant &&
    applicant.application_status === ApplicantStatus.Submitted
  )
    switch (applicant.application_fee_status) {
      case ApplicationFeeStatus.Refunded:
        return "Refund has already been requested for this application";
      case ApplicationFeeStatus.Paid:
        return undefined;
      default:
        return `You can request a refund if the ${role} has paid the application fee`;
    }
};

export const ApplicantHeaderCell = ({
  applicant,
  applicationId,
  pendingMagicLink,
  setCancelApplicantModal,
  setUncancelApplicantModal,
  setChangeRoleModal,
  setRefundModal,
  setResendInviteModal,
  applicationStatus,
  allowCoSigner,
  allowOccupant,
  setManualApplicationOpen
}: {
  applicant: ApplicantOrInvitee;
  applicationId?: string;
  pendingMagicLink?: Invitee;
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setUncancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  setResendInviteModal?: Dispatch<SetStateAction<Invitee | undefined>>;
  applicationStatus?: ApplicationStatus;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  setManualApplicationOpen: (applicantId: number) => void;
}) => {
  const { permissions, type, partnerFeatures } = useAuthContext();
  const { adminPrivileges } = type;
  const navigate = useNavigate();
  // TODO: disable chat for manual applicants
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);

  const { client } = useChatContext();

  const channel =
    "verifications" in applicant
      ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- that's expected
        client?.getChannelById("messaging", `chat-id-${applicant.id}`, {
          watch: true
        })
      : undefined;

  const [unreads, setUnreads] = useState(0);

  useEffect(() => {
    const channelWatcher = async () => {
      if (channel) {
        await channel.watch();

        setUnreads(channel.countUnread());

        channel.on(() => {
          setUnreads(channel.countUnread());
        });
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- expected
    if (channel) channelWatcher();
  }, [channel]);

  const applicantFirstName =
    "first_name" in applicant ? (applicant.first_name ?? undefined) : undefined;
  const applicantLastName =
    "last_name" in applicant ? (applicant.last_name ?? undefined) : undefined;
  const applicantMiddleName =
    "middle_name" in applicant
      ? (applicant.middle_name ?? undefined)
      : undefined;
  const inviteFirstName =
    pendingMagicLink && "first_name" in pendingMagicLink
      ? (pendingMagicLink.first_name ?? undefined)
      : undefined;
  const inviteLastName =
    pendingMagicLink && "last_name" in pendingMagicLink
      ? (pendingMagicLink.last_name ?? undefined)
      : undefined;
  const inviteMiddleName =
    pendingMagicLink && "middle_name" in pendingMagicLink
      ? (pendingMagicLink.middle_name ?? undefined)
      : undefined;

  const isApplicant = "verifications" in applicant;
  const isManual = "is_manual" in applicant && applicant.is_manual;

  const effectiveStatus = resolveEffectiveStatus({
    applicant,
    pendingMagicLink
  });

  const isApplicationFinalized =
    applicationStatus !== undefined &&
    FINAL_APPLICATION_STATES.has(applicationStatus as ApplicationStatus);

  const isCancelApplicantDisabled =
    isApplicationFinalized || effectiveStatus === ApplicantStatus.Canceled;

  const hideCancelApplicant =
    applicationStatus === ApplicationStatus.Canceled ||
    effectiveStatus === ApplicantStatus.Canceled;

  const changeRoleTooltip = getChangeRoleTooltip({
    adminPrivileges,
    canChangeApplicantRole: !!permissions.canChangeApplicantRole,
    isApplicant,
    applicant,
    allowCoSigner,
    allowOccupant,
    effectiveStatus
  });

  const chatLinkTo = isApplicant
    ? `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=chat`
    : "";

  // eslint-disable-next-line sonarjs/cognitive-complexity -- this is much better than before
  const popoverActions = useMemo(() => {
    const actions: Action[] = [];

    const isApplicantEntity = "verifications" in applicant;
    const applicantId = isApplicantEntity
      ? String(applicant.id)
      : String(pendingMagicLink?.id);

    const isPendingMagicLinkActive =
      pendingMagicLink?.active &&
      !pendingMagicLink.used &&
      ![
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved,
        ApplicationStatus.Canceled,
        ApplicationStatus.OnHold
      ].includes(applicationStatus || ("" as ApplicationStatus));

    const canChangeRole =
      (adminPrivileges || permissions.canChangeApplicantRole) &&
      (allowCoSigner || allowOccupant) &&
      isApplicantEntity &&
      applicant.first_name &&
      effectiveStatus &&
      [ApplicantStatus.Submitted, ApplicantStatus.Canceled].includes(
        effectiveStatus
      );

    const canCancel =
      (adminPrivileges || permissions.canCancelApplications) &&
      !hideCancelApplicant &&
      ![
        ApplicationStatus.Canceled,
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved
      ].includes(applicationStatus as ApplicationStatus) &&
      "id" in applicant;

    const showUncancel =
      (adminPrivileges || permissions.canCancelApplications) &&
      hideCancelApplicant;

    const isRefundable =
      "paid_application_fee" in applicant &&
      applicant.application_status === ApplicantStatus.Submitted &&
      applicant.application_fee_status === ApplicationFeeStatus.Paid &&
      (applicant.paid_application_fee?.cents || 0) > 0;

    const iconLabel = (
      icon: IconSvgElement,
      label: string,
      color = "stroke-content-200"
    ) => (
      <span className="flex gap-2 items-center">
        <HugeiconsIcon icon={icon} size={20} className={color} />
        <p>{label}</p>
      </span>
    );

    const add = (action: Action, condition = true) =>
      condition && actions.push(action);

    const warnIfNoApplication = () => {
      if (!applicationId)
        toast.warning("Applicant received an invite, but didn't apply yet");
      return Boolean(applicationId);
    };

    add(
      {
        title: iconLabel(
          Navigation03Icon,
          "Resend invite",
          "stroke-content-100"
        ),
        onClick: () => setResendInviteModal?.(pendingMagicLink)
      },
      isPendingMagicLinkActive
    );

    add({
      title: iconLabel(ArrowRight02Icon, "Review", "stroke-content-100"),
      disabled: !isApplicant,
      tooltip: isApplicant
        ? undefined
        : "Invited applications don't have any data to review",
      onClick: () => {
        if (!warnIfNoApplication()) return;
        if (isApplicantEntity)
          navigate(
            `/applications/${applicationId}/reports?applicantId=${applicant.id}`
          );
      }
    });

    add(
      {
        title: iconLabel(UserIcon, "Change role"),
        disabled: !canChangeRole,
        tooltip: changeRoleTooltip,
        onClick: () =>
          isApplicant && setChangeRoleModal(applicant as ApplicantOrInvitee)
      },
      isApplicantEntity
    );

    add(
      {
        title: iconLabel(Attachment02Icon, "Upload documents"),
        disabled: !isApplicant,
        tooltip:
          "Applicants need to start their application before documents can be uploaded",
        onClick: () => {
          if (!warnIfNoApplication()) return;
          if (isApplicantEntity)
            navigate(
              `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=additional_documents`
            );
        }
      },
      adminPrivileges
    );

    add(
      showUncancel
        ? {
            title: iconLabel(Archive03Icon, "Uncancel application"),
            disabled:
              applicationStatus === ApplicationStatus.OnHold ||
              isApplicationFinalized,
            tooltip: isApplicationFinalized
              ? "Application has already been decisioned. To uncancel applicant, you'll first need to undo decision for the application."
              : "You need to resume the application before uncanceling",
            onClick: () => setUncancelApplicantModal(applicantId)
          }
        : {
            title: iconLabel(Archive03Icon, "Cancel application"),
            disabled: isCancelApplicantDisabled,
            tooltip: hideCancelApplicant
              ? "Can not cancel a canceled application"
              : "You can't cancel application once the decisions have been made",
            onClick: () => canCancel && setCancelApplicantModal(applicantId)
          }
    );

    if (adminPrivileges) {
      const canPrint =
        "application_submitted_at" in applicant &&
        applicant.application_fee_status !== ApplicationFeeStatus.Refunded &&
        applicant.application_submitted_at &&
        (applicant.paid_application_fee?.cents || 0) > 0;

      add({
        title: iconLabel(
          ReceiptDollarIcon,
          "Print receipt",
          "text-content-200"
        ),
        disabled: !canPrint,
        tooltip:
          "application_submitted_at" in applicant &&
          applicant.application_submitted_at
            ? "Receipt can only be printed for paid applicants"
            : "Please wait until applicant submits their application",
        onClick: () => {
          if (!isApplicantEntity) toast.error("Unable to print receipt");
          else
            window.open(
              `/applications/${applicationId}/reports/print/receipt?applicantId=${applicant.id}`,
              "_blank"
            );
        }
      });
    }

    add(
      {
        title: iconLabel(
          CashbackIcon,
          "Refund application",
          "text-content-200"
        ),
        disabled: !isRefundable,
        tooltip: getRefundTooltip(applicant),
        onClick: () => isApplicantEntity && setRefundModal(applicantId)
      },
      adminPrivileges
    );

    return actions;
  }, [
    pendingMagicLink,
    applicationStatus,
    applicant,
    applicationId,
    adminPrivileges,
    permissions,
    isApplicant,
    allowCoSigner,
    allowOccupant,
    effectiveStatus,
    changeRoleTooltip,
    hideCancelApplicant,
    isCancelApplicantDisabled
  ]);

  const [finished, total] = useMemo(() => {
    // If application is finished or submitted -> simulate 100% completion
    // There are cases when count comes incorrectly from the backend,
    if (
      [ApplicantStatus.Finished, ApplicantStatus.Submitted].includes(
        applicant.application_status
      )
    )
      return [10, 10];

    if ("finished_verifications" in applicant)
      return [
        applicant.finished_verifications.finished_steps,
        applicant.finished_verifications.total_steps
      ];

    // if applicant is invitee -> simulate 0%
    return [0, 10];
  }, [applicant]);

  return (
    <Cell className="!h-[176px] relative !py-6" isDataCell>
      <div className="w-full">
        <div className="flex justify-center">
          <div>
            <h3 className="text-sm font-bold text-content-1000 mb-2 truncate max-w-[240px]">
              {(() => {
                const hasApplicantName = Boolean(
                  applicantFirstName || applicantLastName || applicantMiddleName
                );
                const hasInviteName = Boolean(
                  inviteFirstName || inviteLastName || inviteMiddleName
                );

                if (hasApplicantName && applicantFirstName && applicantLastName)
                  return getFullName({
                    firstName: applicantFirstName,
                    lastName: applicantLastName,
                    middleName: applicantMiddleName,
                    short: true
                  });

                if (hasInviteName && inviteFirstName && inviteLastName)
                  return getFullName({
                    firstName: inviteFirstName,
                    lastName: inviteLastName || pendingMagicLink?.email || "",
                    middleName: inviteMiddleName || undefined,
                    short: true
                  });

                return pendingMagicLink?.email || applicant.email;
              })()}
            </h3>
            <p className="text-sm text-content-400">
              {isManual ? "Manual application" : "Self-serve application"}
            </p>
          </div>

          <div className="ml-auto">
            <ContextMenuPopover
              contentClassName="z-[12]"
              triggerClassName="border-none !shadow-none"
              borderLess={false}
              actions={popoverActions}
            />
          </div>
        </div>

        <VerificationProgress
          className="my-4"
          status={effectiveStatus || ApplicantStatus.Pending}
          completedVerifications={finished}
          totalVerifications={total}
        />

        {isManual &&
        !(
          effectiveStatus &&
          [ApplicantStatus.Canceled, ApplicantStatus.Submitted].includes(
            effectiveStatus
          )
        ) &&
        hasActionInManualApplication({
          ...applicant,
          verifications: applicant.verifications.flat()
        }) ? (
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            className="mt-2 w-full"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setManualApplicationOpen(Number(applicant.id));
            }}
          >
            {applicant.application_started_at
              ? "Complete application"
              : "Start application"}
          </Button>
        ) : null}
        {isApplicant &&
        !(
          applicant.is_manual &&
          [
            ApplicantStatus.Pending,
            ApplicantStatus.Finished,
            ApplicantStatus.Started
          ].includes(applicant.application_status)
        ) ? (
          <div className="flex items-center gap-2">
            <Button size={ButtonSize.SM} className="w-full">
              Review
            </Button>
            <Show
              if={
                hasChat &&
                isApplicant &&
                !applicant.is_manual &&
                applicant.application_started_at
              }
            >
              <Link to={chatLinkTo} className="relative shrink-0 block">
                <IconButton
                  HugeIcon={BubbleChatIcon}
                  size={IconButtonSize.SM}
                  className="block"
                />
                {unreads ? (
                  <div className="bg-primary-500 text-white rounded text-xxs px-1 ml-auto min-w-[16px] text-center absolute -top-1.5 -right-1 outline outline-white">
                    {compactNumber(unreads)}
                  </div>
                ) : null}
              </Link>
            </Show>
          </div>
        ) : null}
      </div>
    </Cell>
  );
};
